import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';

import type { TaskTabType } from '@/types';

interface DashboardTabsProps {
  activeTab: TaskTabType;
  onTabChange: (tab: TaskTabType) => void;
  moveTaskCount?: number | undefined;
  auditTaskCount?: number | undefined;
}

/**
 * Bottom navigation tab bar matching the Xamarin DashboardPage TabHostView.
 *
 * Xamarin layout (DashboardPage.xaml):
 *  - TabHostView: HeightRequest=60, CornerRadius=0, IsSegmented, SegmentedHasSeparator
 *  - SegmentedTabStyle: SelectedTabColor=#003366, FontFamily=PoppinsRegular
 *  - Icons: HeightRequest=30, Aspect=AspectFit
 *  - Labels: FontSize=12 (Phone), PoppinsRegular
 *  - MY TASKS tab: horizontal layout (icon+label stacked) + (M/A badge stack at end)
 *  - UPCOMING / COMPLETED tabs: vertical icon + label, Spacing=0
 *  - Badges: Frame CornerRadius=10, Padding=0, Label Margin="6,1", FontSize=12
 *  - MoveTaskBackgroundColor=#E38C25, AuditTaskBackgroundColor=#273F94
 */
export function DashboardTabs({
  activeTab,
  onTabChange,
  moveTaskCount,
  auditTaskCount,
}: DashboardTabsProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      <div className="flex h-[60px] bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.15)]">
        {/* ── MY TASKS tab — Xamarin: Horizontal StackLayout, icon+label center, badges end ── */}
        <button
          onClick={() => {
            onTabChange('myTasks');
          }}
          className={`flex flex-1 items-center px-2 transition-colors ${
            activeTab === 'myTasks' ? 'bg-[#003366]' : 'bg-white'
          }`}
        >
          {/* Icon + label group — centered in remaining space */}
          <div className="flex flex-1 flex-col items-center justify-center">
            <img
              src={
                activeTab === 'myTasks'
                  ? IMAGE_CONSTANTS.myTaskSelected
                  : IMAGE_CONSTANTS.myTaskUnselected
              }
              alt="My Tasks"
              className="h-[30px] w-[30px] object-contain"
            />
            <span
              className={`text-sm font-poppins leading-tight ${activeTab === 'myTasks' ? 'text-white' : 'text-black'}`}
            >
              {t('dashboard.tabs.myTasks')}
            </span>
          </div>

          {/* M/A badges — pushed to end, stretch to fill tab height, gap between badges only */}
          <div className="flex flex-col justify-center gap-1.5 self-stretch pr-0.5">
            <span className="inline-flex flex-1 items-center justify-center rounded-[10px] bg-[#E38C25] px-[8px] text-sm font-poppins leading-tight text-white">
              M : {moveTaskCount ?? 0}
            </span>
            <span className="inline-flex flex-1 items-center justify-center rounded-[10px] bg-[#273F94] px-[8px] text-sm font-poppins leading-tight text-white">
              A : {auditTaskCount ?? 0}
            </span>
          </div>
        </button>

        {/* Separator — Xamarin: SegmentedHasSeparator=True */}
        <div className="w-px self-stretch bg-gray-200" />

        {/* ── UPCOMING tab — Xamarin: Vertical StackLayout, Spacing=0 ── */}
        <button
          onClick={() => {
            onTabChange('upcoming');
          }}
          className={`flex flex-1 flex-col items-center justify-center transition-colors ${
            activeTab === 'upcoming' ? 'bg-[#003366]' : 'bg-white'
          }`}
        >
          {/* Xamarin: SvgCachedImage HeightRequest=30, Aspect=AspectFit */}
          <img
            src={
              activeTab === 'upcoming'
                ? IMAGE_CONSTANTS.upcomingSelected
                : IMAGE_CONSTANTS.upcomingUnselected
            }
            alt="Upcoming"
            className="h-[30px] w-[30px] object-contain"
          />
          {/* Xamarin: FontSize=12, PoppinsRegular */}
          <span
            className={`text-sm font-poppins leading-tight ${activeTab === 'upcoming' ? 'text-white' : 'text-black'}`}
          >
            {t('dashboard.tabs.upcoming')}
          </span>
        </button>

        {/* Separator */}
        <div className="w-px self-stretch bg-gray-200" />

        {/* ── COMPLETED tab — Xamarin: Vertical StackLayout, Spacing=0 ── */}
        <button
          onClick={() => {
            onTabChange('completed');
          }}
          className={`flex flex-1 flex-col items-center justify-center transition-colors ${
            activeTab === 'completed' ? 'bg-[#003366]' : 'bg-white'
          }`}
        >
          {/* Xamarin: SvgCachedImage HeightRequest=30, Aspect=AspectFit */}
          <img
            src={
              activeTab === 'completed'
                ? IMAGE_CONSTANTS.completedSelected
                : IMAGE_CONSTANTS.completedUnselected
            }
            alt="Completed"
            className="h-[30px] w-[30px] object-contain"
          />
          {/* Xamarin: FontSize=12, PoppinsRegular */}
          <span
            className={`text-sm font-poppins leading-tight ${activeTab === 'completed' ? 'text-white' : 'text-black'}`}
          >
            {t('dashboard.tabs.completed')}
          </span>
        </button>
      </div>
    </div>
  );
}
